import React, { useState, useEffect } from 'react';
import { getLiveDashboard } from '../api';

function LiveDashboard() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState('all');
  // Get tenant_id from logged-in user
const user = JSON.parse(localStorage.getItem('user') || '{}');
const tenantId = user.tenant_id || 1;

  const fetchData = async () => {
    try {
      const response = await getLiveDashboard(tenantId);
      setLocations(response.data.filter(loc => !loc.scheduling_only));
      setError(null);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  const calculateMinRevenue = (location) => {
    const freeCars = parseInt(location.free_cars) || 0;
    const paidCars = parseInt(location.paid_cars) || 0;
    const pricePerCar = parseFloat(location.price_per_car) || 0;
    const paidCarCost = parseFloat(location.paid_car_cost) || 0;
    
    return (freeCars * pricePerCar) + (paidCars * (pricePerCar - paidCarCost));
  };

  const getTotalActualRevenue = (location) => {
    const cash = parseFloat(location.cash_collected) || 0;
    const card = parseFloat(location.card_collected) || 0;
    const venmo = parseFloat(location.venmo_collected) || 0;
    return cash + card + venmo;
  };

  const getSelectedLocationData = () => {
    if (selectedLocation === 'all') return null;
    return locations.find(loc => loc.location_id === parseInt(selectedLocation));
  };

  const selectedLocationData = getSelectedLocationData();

  const totals = locations.reduce((acc, loc) => {
    return {
      totalCars: acc.totalCars + (parseInt(loc.total_cars) || 0),
      carsOnLot: acc.carsOnLot + (parseInt(loc.cars_on_lot) || 0),
      employees: acc.employees + (parseInt(loc.current_employees) || 0),
      minRevenue: acc.minRevenue + calculateMinRevenue(loc),
      actualRevenue: acc.actualRevenue + getTotalActualRevenue(loc)
    };
  }, { totalCars: 0, carsOnLot: 0, employees: 0, minRevenue: 0, actualRevenue: 0 });

  const maxCars = Math.max(...locations.map(loc => parseInt(loc.total_cars) || 0), 1);
  const maxRevenue = Math.max(...locations.map(loc => calculateMinRevenue(loc)), 1);

  if (loading) {
    return (
      <div style={{ padding: '48px' }}>
        <p className="text-gray-600">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '48px', paddingBottom: '40px' }}>
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Live Dashboard</h1>
          <p className="text-gray-600 mt-1">Real-time view of all locations</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500 mb-2">Auto-refreshes every minute</p>
          <button 
            onClick={fetchData}
            style={{
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              padding: '8px 16px',
              borderRadius: '6px',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Refresh Now
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Location Navigator */}
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        padding: '12px',
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        overflowX: 'auto'
      }}>
        <button
          onClick={() => setSelectedLocation('all')}
          style={{
            padding: '10px 20px',
            borderRadius: '8px',
            border: selectedLocation === 'all' ? 'none' : '1px solid #e5e7eb',
            cursor: 'pointer',
            backgroundColor: selectedLocation === 'all' ? '#3b82f6' : '#ffffff',
            color: selectedLocation === 'all' ? '#ffffff' : '#374151',
            fontWeight: '600',
            fontSize: '14px',
            whiteSpace: 'nowrap',
            transition: 'all 0.2s'
          }}
        >
          All Locations
        </button>
        <div style={{ width: '1px', height: '24px', backgroundColor: '#e5e7eb', margin: '0 4px' }}></div>
        {locations.map((loc) => (
          <button
            key={loc.location_id}
            onClick={() => setSelectedLocation(loc.location_id.toString())}
            style={{
              padding: '10px 16px',
              borderRadius: '8px',
              border: selectedLocation === loc.location_id.toString() ? 'none' : '1px solid #e5e7eb',
              cursor: 'pointer',
              backgroundColor: selectedLocation === loc.location_id.toString() ? '#3b82f6' : '#ffffff',
              color: selectedLocation === loc.location_id.toString() ? '#ffffff' : '#374151',
              fontWeight: '500',
              fontSize: '14px',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
          >
            {loc.location_name}
            {loc.shift_id && (
              <span style={{
                width: '8px',
                height: '8px',
                backgroundColor: selectedLocation === loc.location_id.toString() ? '#ffffff' : '#22c55e',
                borderRadius: '50%',
                display: 'inline-block'
              }}></span>
            )}
          </button>
        ))}
      </div>

      {/* Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: '16px',
        marginBottom: '32px'
      }}>
        <div className="bg-white rounded-lg shadow p-4" style={{backgroundColor: '#ffffff'}}>
          <p className="text-sm text-gray-500">Total Cars Today</p>
          <p className="text-2xl font-bold text-gray-900">
            {selectedLocation === 'all' ? totals.totalCars : (parseInt(selectedLocationData?.total_cars) || 0)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4" style={{backgroundColor: '#ffffff'}}>
          <p className="text-sm text-gray-500">Cars On Lot</p>
          <p className="text-2xl font-bold text-gray-900">
            {selectedLocation === 'all' ? totals.carsOnLot : (parseInt(selectedLocationData?.cars_on_lot) || 0)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4" style={{backgroundColor: '#ffffff'}}>
          <p className="text-sm text-gray-500">Employees Working</p>
          <p className="text-2xl font-bold text-gray-900">
            {selectedLocation === 'all' ? totals.employees : (parseInt(selectedLocationData?.current_employees) || 0)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4" style={{backgroundColor: '#ffffff'}}>
          <p className="text-sm text-gray-500">Est. Min Revenue</p>
          <p className="text-2xl font-bold" style={{color: '#16a34a'}}>
            ${selectedLocation === 'all' ? totals.minRevenue.toFixed(2) : calculateMinRevenue(selectedLocationData || {}).toFixed(2)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4" style={{backgroundColor: '#ffffff'}}>
          <p className="text-sm text-gray-500">Actual Collected</p>
          <p className="text-2xl font-bold" style={{color: '#16a34a'}}>
            ${selectedLocation === 'all' ? totals.actualRevenue.toFixed(2) : getTotalActualRevenue(selectedLocationData || {}).toFixed(2)}
          </p>
        </div>
      </div>

      {/* All Locations View - Charts */}
      {selectedLocation === 'all' && (
        <div className="grid grid-cols-2 gap-6">
          {/* Cars Today by Location Chart */}
          <div className="bg-white rounded-lg shadow p-6" style={{backgroundColor: '#ffffff'}}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Cars Today by Location</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {locations.map((loc) => {
                const cars = parseInt(loc.total_cars) || 0;
                const percentage = maxCars > 0 ? (cars / maxCars) * 100 : 0;
                return (
                  <div key={loc.location_id}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span className="text-sm text-gray-700">{loc.location_name}</span>
                      <span className="text-sm font-semibold text-gray-900">{cars}</span>
                    </div>
                    <div style={{
                      height: '20px',
                      backgroundColor: '#f3f4f6',
                      borderRadius: '6px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        height: '100%',
                        width: `${percentage}%`,
                        backgroundColor: loc.shift_id ? '#3b82f6' : '#d1d5db',
                        borderRadius: '6px',
                        transition: 'width 0.3s ease'
                      }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Est. Min Revenue by Location Chart */}
          <div className="bg-white rounded-lg shadow p-6" style={{backgroundColor: '#ffffff'}}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Est. Min Revenue by Location</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {locations.map((loc) => {
                const revenue = calculateMinRevenue(loc);
                const percentage = maxRevenue > 0 ? (revenue / maxRevenue) * 100 : 0;
                return (
                  <div key={loc.location_id}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span className="text-sm text-gray-700">{loc.location_name}</span>
                      <span className="text-sm font-semibold" style={{color: '#16a34a'}}>${revenue.toFixed(2)}</span>
                    </div>
                    <div style={{
                      height: '20px',
                      backgroundColor: '#f3f4f6',
                      borderRadius: '6px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        height: '100%',
                        width: `${percentage}%`,
                        backgroundColor: loc.shift_id ? '#22c55e' : '#d1d5db',
                        borderRadius: '6px',
                        transition: 'width 0.3s ease'
                      }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Single Location View */}
      {selectedLocation !== 'all' && selectedLocationData && (
        <div className="bg-white rounded-lg shadow p-6" style={{backgroundColor: '#ffffff'}}>
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{selectedLocationData.location_name}</h2>
              <p className="text-gray-500">${parseFloat(selectedLocationData.price_per_car || 0).toFixed(2)} per car</p>
            </div>
            <div className="flex gap-2">
              {selectedLocationData.shift_id && (
                <span style={{
                  backgroundColor: '#dcfce7',
                  color: '#166534',
                  fontSize: '14px',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontWeight: '500'
                }}>
                  Active Shift
                </span>
              )}
              {selectedLocationData.is_final && (
                <span style={{
                  backgroundColor: '#dbeafe',
                  color: '#1e40af',
                  fontSize: '14px',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontWeight: '500'
                }}>
                  Final Report
                </span>
              )}
            </div>
          </div>

          {/* 4 Stats in a Row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '12px',
            marginBottom: '24px'
          }}>
            <div style={{padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px', textAlign: 'center'}}>
              <p className="text-xs text-gray-500 mb-1">Total Cars</p>
              <p className="text-xl font-bold text-gray-900">{parseInt(selectedLocationData.total_cars) || 0}</p>
            </div>
            <div style={{padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px', textAlign: 'center'}}>
              <p className="text-xs text-gray-500 mb-1">Cars On Lot</p>
              <p className="text-xl font-bold text-gray-900">{parseInt(selectedLocationData.cars_on_lot) || 0}</p>
            </div>
            <div style={{padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px', textAlign: 'center'}}>
              <p className="text-xs text-gray-500 mb-1">Free / Paid</p>
              <p className="text-xl font-bold text-gray-900">
                {parseInt(selectedLocationData.free_cars) || 0} / {parseInt(selectedLocationData.paid_cars) || 0}
              </p>
            </div>
            <div style={{padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px', textAlign: 'center'}}>
              <p className="text-xs text-gray-500 mb-1">Reservations</p>
              <p className="text-xl font-bold text-gray-900">{parseInt(selectedLocationData.reservations) || 0}</p>
            </div>
          </div>

          {/* Staffing */}
          <div style={{ marginBottom: '24px' }}>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Staffing</h3>
            <div style={{padding: '20px', backgroundColor: '#f9fafb', borderRadius: '8px'}}>
              <div className="flex justify-between mb-3">
                <span className="text-gray-600">Currently Working</span>
                <span className="font-bold text-xl">{parseInt(selectedLocationData.current_employees) || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Target Headcount</span>
                <span className="font-bold text-xl">{parseInt(selectedLocationData.target_headcount) || 0}</span>
              </div>
            </div>
          </div>

          {/* Revenue Collected */}
          <div style={{ marginBottom: '24px' }}>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Revenue Collected</h3>
            <div style={{padding: '20px', backgroundColor: '#f9fafb', borderRadius: '8px'}}>
              <div className="flex justify-between mb-3">
                <span className="text-gray-600">Cash</span>
                <span className="font-bold text-xl" style={{color: '#16a34a'}}>${(parseFloat(selectedLocationData.cash_collected) || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Card</span>
                <span className="font-bold text-xl" style={{color: '#16a34a'}}>${(parseFloat(selectedLocationData.card_collected) || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {selectedLocationData.report_time && (
            <p className="text-sm text-gray-400">
              Last updated: {new Date(selectedLocationData.report_time).toLocaleString()}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default LiveDashboard;